import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* -------------------------------------------------------------------------- */
/*              Dashboard — Sky Blue tilt & shimmer dashboard                 */
/* -------------------------------------------------------------------------- */

const CARDS = [
  {
    to: '/ask',
    num: '01',
    title: 'Ask Nitin',
    description: 'Ask anything about your career — internships, practice areas, first jobs — and get straight, experience-backed guidance.',
    tag: 'Insights',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    to: '/sprint',
    num: '02',
    title: 'Sprint',
    description: 'A structured mentorship programme for 1st, 2nd and 3rd year law students. Complete two tasks, earn a Zoom session.',
    tag: 'Mentorship',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
  {
    to: '/assessment',
    num: '03',
    title: 'Find Your Practice Area',
    description: 'A 20-question self-assessment that goes deeper than grades, into your temperament and readiness.',
    tag: 'Assessment',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </svg>
    ),
  },
  {
    to: '/resume',
    num: '04',
    title: 'Resume Builder and Assessment',
    description: 'Craft a legal resume shaped by insider knowledge of what Indian law firms are really looking for.',
    tag: 'Career',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
];

const NOIR_CSS = `
  @keyframes h02-rise {
    from { opacity: 0; transform: translateY(30px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes h02-shine {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes h02-drift {
    0%,100% { transform: translate(0,0); }
    50%     { transform: translate(14px,-18px); }
  }
  @keyframes h02-twinkle {
    0%,100% { opacity: 0.15; transform: scale(0.8); }
    50%     { opacity: 0.8;  transform: scale(1.2); }
  }
  @keyframes h02-scan {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(300%); }
  }

  .h02-root {
    position: relative;
    min-height: calc(100dvh - var(--nav-h));
    background: #dbeeff;
    overflow: hidden;
    color: #0c2340;
  }
  .h02-root::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse 80% 50% at 20% 0%, rgba(56,132,255,0.18), transparent 60%),
      radial-gradient(ellipse 60% 50% at 80% 100%, rgba(12,35,64,0.10), transparent 60%),
      linear-gradient(180deg, #e6f3ff 0%, #c9e3ff 100%);
    pointer-events: none;
  }
  .h02-root::after {
    content: '';
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(12,35,64,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(12,35,64,0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse at top, black 30%, transparent 80%);
    pointer-events: none;
  }

  .h02-stars { position: absolute; inset: 0; pointer-events: none; z-index: 1; }
  .h02-stars span {
    position: absolute;
    width: 3px; height: 3px;
    border-radius: 50%;
    background: #1e3a8a;
    box-shadow: 0 0 10px rgba(30,58,138,0.6);
    animation: h02-twinkle 3.5s ease-in-out infinite;
  }

  .h02-orb {
    position: absolute;
    width: 380px; height: 380px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,0.35), transparent 70%);
    filter: blur(60px);
    pointer-events: none;
    animation: h02-drift 14s ease-in-out infinite;
    z-index: 1;
  }
  .h02-orb.a { top: -120px; left: -100px; }
  .h02-orb.b { bottom: -160px; right: -120px; background: radial-gradient(circle, rgba(147,197,253,0.55), transparent 70%); animation-duration: 18s; }

  .h02-inner {
    position: relative; z-index: 3;
    max-width: 1080px; margin: 0 auto;
    padding: 44px 22px 60px;
  }

  /* Hero */
  .h02-eyebrow {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 8px 16px;
    border: 1px solid rgba(12,35,64,0.25);
    border-radius: 999px;
    background: rgba(255,255,255,0.55);
    backdrop-filter: blur(6px);
    font-size: 11px; font-weight: 700;
    letter-spacing: 2.5px; text-transform: uppercase;
    color: #0c2340;
    animation: h02-rise 0.7s ease both;
  }
  .h02-eyebrow .line {
    width: 24px; height: 1px;
    background: linear-gradient(90deg, #0c2340, transparent);
  }

  .h02-h1 {
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: clamp(40px, 7vw, 72px);
    line-height: 1.02;
    font-weight: 800;
    margin: 22px 0 16px;
    animation: h02-rise 0.7s ease 0.1s both;
    letter-spacing: -0.5px;
    color: #0c2340;
  }
  .h02-h1 .gold {
    background: linear-gradient(
      90deg,
      #0c2340 0%,
      #1e3a8a 25%,
      #3b82f6 50%,
      #1e3a8a 75%,
      #0c2340 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: h02-shine 5s linear infinite;
    font-style: italic;
  }

  .h02-lede {
    font-size: 15.5px; line-height: 1.75;
    color: rgba(12,35,64,0.72);
    max-width: 640px;
    margin-bottom: 34px;
    animation: h02-rise 0.7s ease 0.18s both;
  }

  .h02-cap {
    display: flex; align-items: center; gap: 14px;
    font-size: 10px; font-weight: 700; letter-spacing: 2.5px;
    text-transform: uppercase; color: rgba(12,35,64,0.65);
    margin-bottom: 18px;
  }
  .h02-cap::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(90deg, rgba(12,35,64,0.35), transparent);
  }

  /* Feature cards */
  .h02-feat {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
    margin-bottom: 40px;
  }
  @media (max-width: 720px) { .h02-feat { grid-template-columns: 1fr; } }

  .h02-card {
    position: relative;
    padding: 28px 26px 26px;
    border-radius: 20px;
    background: linear-gradient(160deg, #ffffff 0%, #eaf4ff 100%);
    border: 1px solid rgba(12,35,64,0.18);
    overflow: hidden;
    text-decoration: none;
    color: #0c2340;
    opacity: 0;
    animation: h02-rise 0.8s ease forwards;
    transition: transform .45s cubic-bezier(.2,.7,.2,1),
                border-color .3s ease,
                box-shadow .35s ease;
    transform-style: preserve-3d;
    perspective: 1000px;
    cursor: pointer;
    min-height: 190px;
    display: flex; flex-direction: column;
  }
  .h02-card::before {
    content: '';
    position: absolute; top: 0; left: 0;
    width: 100%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(12,35,64,0.45), transparent);
  }
  .h02-card::after {
    content: '';
    position: absolute; top: 0; left: -60%;
    width: 40%; height: 100%;
    background: linear-gradient(120deg, transparent, rgba(59,130,246,0.14), transparent);
    transform: skewX(-20deg);
    transition: left .8s ease;
    pointer-events: none;
  }
  .h02-card:hover {
    transform: translateY(-6px);
    border-color: rgba(30,58,138,0.55);
    box-shadow:
      0 28px 60px -18px rgba(12,35,64,0.28),
      0 0 0 1px rgba(30,58,138,0.12) inset;
  }
  .h02-card:hover::after { left: 140%; }

  .h02-card .top {
    display: flex; align-items: center; justify-content: space-between;
    gap: 14px;
    margin-bottom: 12px;
  }
  .h02-card .top h3 { flex: 1; margin-bottom: 0; }
  .h02-card .num {
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 42px; font-weight: 800;
    background: linear-gradient(180deg, #0c2340 0%, rgba(12,35,64,0.12) 100%);
    -webkit-background-clip: text; background-clip: text;
    color: transparent;
    line-height: 0.9;
    letter-spacing: -2px;
  }
  .h02-card .ico-wrap {
    width: 56px; height: 56px;
    border-radius: 14px;
    border: 1px solid rgba(12,35,64,0.2);
    background: linear-gradient(135deg, rgba(59,130,246,0.18), rgba(59,130,246,0.04));
    display: flex; align-items: center; justify-content: center;
    color: #0c2340;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.6) inset;
  }
  .h02-card h3 {
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 22px; font-weight: 700;
    margin-bottom: 8px;
    color: #0c2340;
  }
  .h02-card p {
    font-size: 13px; line-height: 1.65;
    color: rgba(12,35,64,0.65);
    flex: 1;
  }
  .h02-card .foot {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid rgba(12,35,64,0.12);
  }
  .h02-card .tag {
    font-size: 10px; font-weight: 700; letter-spacing: 1.5px;
    text-transform: uppercase; color: rgba(12,35,64,0.55);
  }
  .h02-card .open {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 700;
    color: #0c2340; letter-spacing: 0.6px;
    transition: transform .3s ease;
  }
  .h02-card:hover .open { transform: translateX(4px); color: #1e3a8a; }

  /* Quote strip */
  .h02-quote {
    position: relative;
    padding: 30px 32px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(234,244,255,0.95) 100%);
    border: 1px solid rgba(12,35,64,0.18);
    margin-bottom: 36px;
    overflow: hidden;
    animation: h02-rise 0.8s ease 0.25s both;
  }
  .h02-quote::before {
    content: '"';
    position: absolute; top: -30px; left: 16px;
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 180px;
    color: rgba(12,35,64,0.08);
    line-height: 1;
  }
  .h02-quote p {
    position: relative;
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 19px; font-style: italic; line-height: 1.55;
    color: #0c2340;
    margin-bottom: 14px;
  }
  .h02-quote .sig {
    position: relative;
    font-size: 12px; font-weight: 600; letter-spacing: 1.5px;
    text-transform: uppercase;
    background: linear-gradient(90deg, #0c2340, #3b82f6);
    -webkit-background-clip: text; background-clip: text;
    color: transparent;
  }

  /* Intro (collapsible) */
  .h02-intro { margin-top: 8px; margin-bottom: 36px; }
  .h02-intro p {
    font-family: 'DM Sans', sans-serif;
    font-style: normal;
    font-size: 14.5px;
    line-height: 1.75;
    color: rgba(12,35,64,0.82);
    margin-bottom: 10px;
  }
  .h02-intro p.dear {
    font-family: 'DM Sans', -apple-system, sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #0c2340;
    margin-bottom: 12px;
  }
  .h02-intro p em { font-style: italic; color: #1e3a8a; }
  .h02-intro p.welcome {
    font-style: italic;
    color: rgba(12,35,64,0.65);
    margin-top: 6px;
  }
  .h02-intro .more {
    display: inline-block;
    margin-top: 6px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #1e3a8a;
    cursor: pointer;
    border-bottom: 1.5px solid rgba(30,58,138,0.4);
    padding-bottom: 1px;
    transition: border-color .2s ease, color .2s ease;
  }
  .h02-intro .more:hover { color: #0c2340; border-bottom-color: #0c2340; }

  /* Stats */
  .h02-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 28px;
    animation: h02-rise 0.8s ease 0.32s both;
  }
  @media (max-width: 540px) { .h02-stats { grid-template-columns: 1fr; } }
  .h02-stat {
    position: relative;
    padding: 18px 22px;
    border-radius: 16px;
    background: linear-gradient(135deg, #ffffff, #eaf4ff);
    border: 1px solid rgba(12,35,64,0.18);
    overflow: hidden;
  }
  .h02-stat::after {
    content: '';
    position: absolute; top: 0; left: -100%;
    width: 40%; height: 2px;
    background: linear-gradient(90deg, transparent, #1e3a8a, transparent);
    animation: h02-scan 3.2s ease-in-out infinite;
  }
  .h02-stat .l {
    font-size: 10px; font-weight: 700; letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(12,35,64,0.55);
    margin-bottom: 6px;
  }
  .h02-stat .v { font-size: 15px; font-weight: 600; color: #0c2340; }

  .h02-admin {
    grid-column: 1/-1;
    border-color: rgba(185,28,28,0.35) !important;
  }
  .h02-admin .ico-wrap {
    border-color: rgba(185,28,28,0.5);
    background: linear-gradient(135deg, rgba(185,28,28,0.15), rgba(185,28,28,0.02));
    color: #b91c1c;
  }
  .h02-admin .tag,
  .h02-admin .open { color: #b91c1c; }
  .h02-admin .num {
    background: linear-gradient(180deg, #b91c1c 0%, rgba(185,28,28,0.12) 100%);
    -webkit-background-clip: text; background-clip: text;
    color: transparent;
  }
`;

export default function Dashboard() {
  const { user } = useAuth();
  const firstName = user?.name ? user.name.split(' ')[0] : '';
  const starsRef = useRef(null);

  const hasSeenIntro = typeof window !== 'undefined' && localStorage.getItem('asknitin_intro_seen');
  const [introExpanded, setIntroExpanded] = useState(!hasSeenIntro);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById('h02-style')) {
      const tag = document.createElement('style');
      tag.id = 'h02-style';
      tag.textContent = NOIR_CSS;
      document.head.appendChild(tag);
    }
    if (!hasSeenIntro) {
      localStorage.setItem('asknitin_intro_seen', '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate random twinkling stars once
  const stars = React.useMemo(() => {
    const arr = [];
    for (let i = 0; i < 32; i++) {
      arr.push({
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 3.5}s`,
        size: 2 + Math.random() * 2,
      });
    }
    return arr;
  }, []);

  // 3D tilt handler
  const handleTilt = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `translateY(-6px) rotateX(${-y * 6}deg) rotateY(${x * 8}deg)`;
  };
  const resetTilt = (e) => {
    e.currentTarget.style.transform = '';
  };

  const isAdmin = ['admin', 'superadmin'].includes(user?.role);

  return (
    <div className="h02-root">
      <div className="h02-orb a" />
      <div className="h02-orb b" />
      <div className="h02-stars" ref={starsRef}>
        {stars.map((s, i) => (
          <span
            key={i}
            style={{
              top: s.top, left: s.left,
              width: s.size, height: s.size,
              animationDelay: s.delay,
            }}
          />
        ))}
      </div>

      <div className="h02-inner">
        {/* Intro */}
        <div className="h02-quote h02-intro">
          <p className="dear">{firstName ? `Dear ${firstName},` : 'Dear Young Lawyers,'}</p>
          <p><strong>Every student I have ever spoken to have the same problem, not a lack of ability, but a lack of someone who would speak to them honestly.</strong></p>
          <p><strong>This platform is my attempt to be that person, at scale.</strong></p>
          {introExpanded && (
            <>
              <p>Ask Nitin is the digital home of my programme <em>Conversations with Nitin Potdar</em>. For years I have been meeting law students one on one, listening to their questions, their confusion and their anxieties, and trying to give them the honest, practical guidance that most law schools simply do not provide. I deeply value every one of those conversations. But I am physically unable to meet everyone personally. This is my answer to that.</p>
              <p>You will find five tools here. Each one addresses a question I am asked constantly. And all my answers strictly based on my book <em>Companion Hand Book for Law Students - what they don't teach at the law schools in India</em> &amp; <em>GPS Paradigm</em> and my several LinkedIn Posts and the Conversations that I had with Law Students.</p>
              <p className="welcome">Welcome. I am glad you are here.<br />– Nitin Potdar</p>
            </>
          )}
          <span className="more" onClick={() => setIntroExpanded(v => !v)}>
            {introExpanded ? 'Show less ↑' : 'Read more →'}
          </span>
        </div>

        {/* Features */}
        <p className="h02-cap">The Tools</p>
        <div className="h02-feat">
          {CARDS.map((c, i) => (
            <Link
              key={c.to}
              to={c.to}
              className="h02-card"
              style={{ animationDelay: `${0.28 + i * 0.08}s` }}
              onMouseMove={handleTilt}
              onMouseLeave={resetTilt}
            >
              <div className="top">
                <h3>{c.title}</h3>
                <div className="ico-wrap">{c.icon}</div>
              </div>
              <p>{c.description}</p>
              <div className="foot">
                <span className="tag">{c.tag}</span>
                <span className="open">
                  Enter
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}

          <Link
            to="/mock-interview"
            className="h02-card"
            style={{ animationDelay: `${0.28 + CARDS.length * 0.08}s` }}
            onMouseMove={handleTilt}
            onMouseLeave={resetTilt}
          >
            <div className="top">
              <h3>Mock Interview with Nitin</h3>
              <div className="ico-wrap">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
              </div>
            </div>
            <p>A real conversation, a real note, and a real sense of where you stand.</p>
            <div className="foot">
              <span className="tag">Interview Prep</span>
              <span className="open">
                Enter
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </div>
          </Link>

          <Link
              to="/cdp"
              className="h02-card"
              style={{ animationDelay: `${0.28 + CARDS.length * 0.08}s` }}
              onMouseMove={handleTilt}
              onMouseLeave={resetTilt}
            >
              <div className="top">
                <h3>Commercial Drafting Programme</h3>
                <div className="ico-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M9 15l2 2 4-4" />
                  </svg>
                </div>
              </div>
              <p>Commercial Drafting — Certified Practice Session. Seven documents, real-world scenarios, three certificate levels.</p>
              <div className="foot">
                <span className="tag">Drafting</span>
                <span className="open">
                  Enter
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </span>
              </div>
            </Link>

          <Link
            to="/account"
            className="h02-card"
            style={{ animationDelay: `${0.28 + (CARDS.length + 1) * 0.08}s` }}
            onMouseMove={handleTilt}
            onMouseLeave={resetTilt}
          >
            <div className="top">
              <h3>My Profile</h3>
              <div className="ico-wrap">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            </div>
            <p>Update your details, manage preferences, and keep your account up to date.</p>
            <div className="foot">
              <span className="tag">Account</span>
              <span className="open">
                Enter
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </div>
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              className="h02-card h02-admin"
              style={{ animationDelay: `${0.28 + CARDS.length * 0.08}s` }}
              onMouseMove={handleTilt}
              onMouseLeave={resetTilt}
            >
              <div className="top">
                <h3>Admin Panel</h3>
                <div className="ico-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
                  </svg>
                </div>
              </div>
              <p>Manage users, upload knowledge bases, and view platform analytics.</p>
              <div className="foot">
                <span className="tag">Admin</span>
                <span className="open">
                  Enter
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </span>
              </div>
            </Link>
          )}
        </div>

        {/* Stats */}
        <p className="h02-cap">Your Account</p>
        <div className="h02-stats">
          <div className="h02-stat">
            <p className="l">Phone</p>
            <p className="v">{user?.phone || '-'}</p>
          </div>
          <div className="h02-stat">
            <p className="l">Role</p>
            <p className="v" style={{ textTransform: 'capitalize' }}>{user?.role || '-'}</p>
          </div>
          <div className="h02-stat">
            <p className="l">Member since</p>
            <p className="v">
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                : 'Today'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
